// Types mirroring backend models from src/graham/data_types/patient_apcm_state.py

export enum APCMState {
  // Initial
  ENROLLMENT_CONFIRMED = "ENROLLMENT_CONFIRMED",
  CONSENT_NEEDED = "CONSENT_NEEDED",
  // Consent scheduling
  CONSENT_SCHEDULED = "CONSENT_SCHEDULED",
  // Consent outcomes
  CONSENT_OBTAINED = "CONSENT_OBTAINED",
  CONSENT_REFUSED = "CONSENT_REFUSED",
  CONSENT_MAX_RETRIES_REACHED = "CONSENT_MAX_RETRIES_REACHED",
  // Care plan lifecycle
  CARE_PLAN_PENDING = "CARE_PLAN_PENDING",
  CARE_PLAN_GENERATING = "CARE_PLAN_GENERATING",
  CARE_PLAN_PENDING_APPROVAL = "CARE_PLAN_PENDING_APPROVAL",
  CARE_PLAN_APPROVED = "CARE_PLAN_APPROVED",
  CARE_PLAN_UPLOADING = "CARE_PLAN_UPLOADING",
  CARE_PLAN_UPLOADED = "CARE_PLAN_UPLOADED",
  CARE_PLAN_FAILED = "CARE_PLAN_FAILED",
  // Check-in
  CHECK_IN_NEEDED = "CHECK_IN_NEEDED",
  CHECK_IN_SCHEDULED = "CHECK_IN_SCHEDULED",
  CHECK_IN_COMPLETED = "CHECK_IN_COMPLETED",
  CHECK_IN_NO_ANSWER = "CHECK_IN_NO_ANSWER",
  CHECK_IN_CANCELLED = "CHECK_IN_CANCELLED",
  /** This month's check-in attempts are used up (D14); the next month starts a new cycle. */
  CHECK_IN_MAX_RETRIES_REACHED = "CHECK_IN_MAX_RETRIES_REACHED",
  // Terminal
  DISENROLLED = "DISENROLLED",
  BILLING_COMPLETED = "BILLING_COMPLETED",
  // Error
  SCHEDULING_FAILED = "SCHEDULING_FAILED",
}

export type StatePhase = "enrollment" | "consent" | "carePlan" | "checkIn" | "terminal"

export const STATE_PHASES: Record<StatePhase, APCMState[]> = {
  enrollment: [APCMState.ENROLLMENT_CONFIRMED],
  consent: [
    APCMState.CONSENT_NEEDED,
    APCMState.CONSENT_SCHEDULED,
    APCMState.CONSENT_OBTAINED,
  ],
  carePlan: [
    APCMState.CARE_PLAN_PENDING,
    APCMState.CARE_PLAN_GENERATING,
    APCMState.CARE_PLAN_PENDING_APPROVAL,
    APCMState.CARE_PLAN_APPROVED,
    APCMState.CARE_PLAN_UPLOADING,
    APCMState.CARE_PLAN_UPLOADED,
    APCMState.CARE_PLAN_FAILED,
  ],
  checkIn: [
    APCMState.CHECK_IN_NEEDED,
    APCMState.CHECK_IN_SCHEDULED,
    APCMState.CHECK_IN_COMPLETED,
    APCMState.CHECK_IN_NO_ANSWER,
    APCMState.CHECK_IN_CANCELLED,
    APCMState.CHECK_IN_MAX_RETRIES_REACHED,
  ],
  terminal: [
    APCMState.CONSENT_REFUSED,
    APCMState.CONSENT_MAX_RETRIES_REACHED,
    APCMState.DISENROLLED,
    APCMState.BILLING_COMPLETED,
    APCMState.SCHEDULING_FAILED,
  ],
}

export const PHASE_LABELS: Record<StatePhase, string> = {
  enrollment: "Enrollment",
  consent: "Consent",
  carePlan: "Care Plan",
  checkIn: "Check-in",
  terminal: "Terminal",
}

export type StateColor = "emerald" | "blue" | "amber" | "red" | "gray"

export const STATE_COLORS: Record<APCMState, StateColor> = {
  [APCMState.ENROLLMENT_CONFIRMED]: "blue",
  [APCMState.CONSENT_NEEDED]: "amber",
  [APCMState.CONSENT_SCHEDULED]: "blue",
  [APCMState.CONSENT_OBTAINED]: "emerald",
  [APCMState.CONSENT_REFUSED]: "red",
  [APCMState.CONSENT_MAX_RETRIES_REACHED]: "red",
  [APCMState.CARE_PLAN_PENDING]: "amber",
  [APCMState.CARE_PLAN_GENERATING]: "blue",
  [APCMState.CARE_PLAN_PENDING_APPROVAL]: "amber",
  [APCMState.CARE_PLAN_APPROVED]: "emerald",
  [APCMState.CARE_PLAN_UPLOADING]: "blue",
  [APCMState.CARE_PLAN_UPLOADED]: "emerald",
  [APCMState.CARE_PLAN_FAILED]: "red",
  [APCMState.CHECK_IN_NEEDED]: "amber",
  [APCMState.CHECK_IN_SCHEDULED]: "blue",
  [APCMState.CHECK_IN_COMPLETED]: "emerald",
  [APCMState.CHECK_IN_NO_ANSWER]: "amber",
  [APCMState.CHECK_IN_CANCELLED]: "gray",
  [APCMState.CHECK_IN_MAX_RETRIES_REACHED]: "red",
  [APCMState.DISENROLLED]: "gray",
  [APCMState.BILLING_COMPLETED]: "emerald",
  [APCMState.SCHEDULING_FAILED]: "red",
}

// Canonical state sets from backend
export const GENERATE_ELIGIBLE_STATES = new Set([
  APCMState.CONSENT_OBTAINED,
  APCMState.CARE_PLAN_PENDING,
  APCMState.CARE_PLAN_FAILED,
])

export const UPLOAD_ELIGIBLE_STATES = new Set([
  APCMState.CARE_PLAN_APPROVED,
  APCMState.CARE_PLAN_UPLOADING,
])

export const CHECKIN_ELIGIBLE_STATES = new Set([
  APCMState.CARE_PLAN_UPLOADED,
  APCMState.CHECK_IN_NEEDED,
])

// API response types
export interface PatientCurrentState {
  patient_id: string
  organization_id: string
  current_state: string
  last_transition: string
  time_in_state_seconds: number | null
  workflow_id: string | null
  agent_name: string | null
  metadata: Record<string, any>
  patient_name?: string
  phone_number?: string
}

export interface PatientAPCMStateHistory {
  patient_id: string
  organization_id: string
  states: PatientAPCMStateResponse[]
  total_count: number
}

export interface PatientAPCMStateResponse {
  id: string
  patient_id: string
  organization_id: string
  current_state: string
  workflow_id: string | null
  agent_name: string | null
  metadata: Record<string, any>
  created_at: string
}

export interface CheckinReadiness {
  ready: number
  awaiting_health_summary: number
}

export interface LifecycleSummary {
  organization_id: string
  state_counts: Record<string, number>
  checkin_readiness?: CheckinReadiness
  no_state: number
  total_patients: number
}

export interface PendingCarePlan {
  care_plan_id: string
  patient_id: string
  patient_name: string
  provider_name: string | null
  condition_count: number
  care_plan_status: string
  created_at: string
  pdf_url: string | null
}

export interface WorkflowStatus {
  workflow_id: string
  status: string
  patient_id: string
  organization_id: string
  current_patient_state: string | null
  goals: any
  context: any
  created_at: string
  updated_at: string
  paused: boolean
  tasks: any[]
  execution_mode: string
}

export interface BulkStateUpdateRequest {
  patient_ids: string[]
  organization_id: string
  new_state: string
  workflow_id?: string
  agent_name?: string
  metadata?: Record<string, any>
}

export interface BulkUpdateResult {
  total: number
  success: number
  failed: number
  failed_patients: Array<{ patient_id: string; error: string }>
}

// Enriched row for the patient table
export interface ApcmPatientRow {
  patient_id: string
  patient_name: string
  phone_number?: string
  current_state: APCMState
  last_transition: string
  time_in_state_seconds: number | null
  workflow_id: string | null
  agent_name: string | null
}

export type ApcmOpsTab = "pipeline" | "program" | "dashboard" | "patients" | "approvals" | "setup"

export const APCM_OPS_TABS: ApcmOpsTab[] = ["pipeline", "program", "dashboard", "patients", "approvals", "setup"]

export interface SQSQueueHealth {
  reachable: boolean
  messages_available?: number
  messages_in_flight?: number
  error?: string
}

export interface FailureDetail {
  id: string
  agent_name: string
  error_message: string | null
  updated_at: string | null
  patient_id: string | null
}

export interface StuckPatient {
  patient_id: string
  patient_name: string | null
  current_state: string
  since: string
}

export interface VoiceCallerId {
  phone_number: string
  provider: string | null
  is_default: boolean
  registered_in_retell: boolean
}

export interface VoiceProviderReadiness {
  provider: "retell"
  enabled: boolean
  configured: boolean
  caller_ids: VoiceCallerId[]
  pending_calls: {
    total: number
    would_dial: number
    blocked: Array<{ reason: string; count: number }>
  }
  problems: string[]
  ready: boolean
}

export interface PreflightCheck {
  total_patients: number
  patients_with_state: number
  patients_without_state: number
  sqs_health: {
    /** Retired pipecat call queue; older API versions still return it. */
    call_queue?: SQSQueueHealth
    workflow_queue: SQSQueueHealth
  }
  /** Absent on API versions before the Retell readiness check. */
  voice_provider?: VoiceProviderReadiness
  recent_failures_24h: number
  last_failures: FailureDetail[]
  stuck_patients_count: number
  stuck_patients: StuckPatient[]
  state_distribution: Record<string, number>
  all_checks_passed: boolean
}

export interface BulkInitResult {
  total_in_org: number
  already_initialized: number
  newly_initialized: number
  state_breakdown: Record<string, number>
  failed: number
  dry_run?: boolean
}

// Bulk ingestion job types
export interface BulkIngestionJobResult {
  filename: string
  patient_name: string
  patient_id: string | null
  pcp: string | null
  status: "success" | "error"
  error: string | null
}

export interface BulkIngestionJob {
  job_id: string
  organization_id?: string
  status: "pending" | "downloading" | "processing" | "completed" | "failed"
  source_type: string
  source_id?: string
  total_files: number
  processed: number
  failed: number
  skipped: number
  results: BulkIngestionJobResult[]
  error_message: string | null
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
}

export interface BulkIngestionJobSummary {
  job_id: string
  status: string
  source_type: string
  total_files: number
  processed: number
  failed: number
  created_at: string | null
  completed_at: string | null
}
